import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listSites, type SiteRecord } from '@/api/sites';
import { useAuthStore } from '@/store/authStore';

/**
 * Multi-site: resolve WHICH site the current design page is editing, so every look/brand/page edit
 * targets an explicit site instead of silently drifting to the tenant's primary.
 *
 * `activeSiteId` (→ the `X-Site-Id` header) is the source of truth. This hook resolves it to a real
 * site record (falling back to the primary / first site), exposes the tenant's site list, and — so a
 * mutation's `X-Site-Id` header can never be absent while sites exist — writes the resolved site back
 * into `activeSiteId` when it's null. Use `activeSite?.id` as the React Query key for site-scoped
 * reads so the cache is per-site and can't serve one site's data for another.
 */
export function useActiveSite(): {
  activeSiteId: string | null;
  activeSite: SiteRecord | undefined;
  sites: SiteRecord[];
  hasMultiple: boolean;
  isLoading: boolean;
  setActiveSiteId: (id: string | null) => void;
} {
  const activeSiteId = useAuthStore((s) => s.activeSiteId);
  const setActiveSiteId = useAuthStore((s) => s.setActiveSiteId);

  const { data: sites = [], isLoading } = useQuery({
    queryKey: ['my-sites'],
    queryFn: listSites,
    staleTime: 60_000,
  });

  const activeSite = useMemo(
    () => sites.find((s) => s.id === activeSiteId) ?? sites.find((s) => s.is_primary) ?? sites[0],
    [sites, activeSiteId],
  );

  // Keep the header in step with the site we're SHOWING: if nothing is active yet but we have a
  // resolved site, adopt it so a write can't fall back to primary while the UI shows another site.
  useEffect(() => {
    if (!activeSiteId && activeSite) setActiveSiteId(activeSite.id);
  }, [activeSiteId, activeSite, setActiveSiteId]);

  return {
    activeSiteId,
    activeSite,
    sites,
    hasMultiple: sites.length > 1,
    isLoading,
    setActiveSiteId,
  };
}
